import * as Dialog from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { useTranslation } from "@/hooks/use-translation"
import type { CanvasProjectStyles } from "@/types/canvas"

type CanvasStylesModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  styles: CanvasProjectStyles
}

export function CanvasStylesModal({ open, onOpenChange, styles }: CanvasStylesModalProps) {
  const { t } = useTranslation("workspace")

  const rows = [
    { label: t("canvas.styles.visualStyle"), value: styles.visual_rendering_style.trim() },
    { label: t("canvas.styles.genre"), value: styles.genre.trim() },
    { label: t("canvas.styles.tone"), value: styles.tone.trim() },
  ].filter((row) => row.value.length > 0)

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="ui-dialog-overlay fixed inset-0 z-[120] bg-[var(--ui-overlay-heavy)] backdrop-blur-sm" />
        <Dialog.Content className="ui-dialog-content fixed top-1/2 left-1/2 z-[121] flex w-[min(92vw,520px)] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 rounded-lg border border-ui-border p-5 shadow-2xl shadow-black/50 focus:outline-none dark:bg-ui-bg-elevated light:bg-ui-surface">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Dialog.Title className="text-base font-semibold text-ui-text-primary">
                {t("canvas.styles.title")}
              </Dialog.Title>
              <Dialog.Description className="mt-0.5 text-xs text-ui-text-secondary">
                {t("canvas.styles.subtitle")}
              </Dialog.Description>
            </div>
            <Dialog.Close
              type="button"
              className="shrink-0 rounded-lg p-1.5 text-ui-text-secondary hover:bg-ui-surface-raised hover:text-ui-text-primary"
              aria-label={t("canvas.modal.close")}
            >
              <X className="h-5 w-5" />
            </Dialog.Close>
          </div>

          {styles.styles_enabled === false ? (
            <p className="rounded-lg border border-dashed border-ui-border-strong bg-ui-surface-sunken px-3 py-6 text-center text-sm text-ui-text-muted">
              {t("canvas.styles.disabledHint")}
            </p>
          ) : rows.length > 0 ? (
            <dl className="space-y-3">
              {rows.map((row) => (
                <div key={row.label} className="rounded-lg border border-ui-border bg-ui-surface-sunken px-3 py-2.5">
                  <dt className="text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">{row.label}</dt>
                  <dd className="mt-1 text-sm whitespace-pre-wrap text-ui-text-primary">{row.value}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="rounded-lg border border-dashed border-ui-border-strong bg-ui-surface-sunken px-3 py-6 text-center text-sm text-ui-text-muted">
              {t("canvas.styles.empty")}
            </p>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
